import { formatAED, localDate, zonedDateTime, type AdminBilling, type BillingEvent, type BillingInvoiceView } from "@teamna/shared";
import { useEffect, useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  DataTable,
  ErrorState,
  Field,
  InlineError,
  Panel,
  RadioGroup,
  SegmentedControl,
  SelectField,
  SkeletonList,
  TextAreaField,
  showToast,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { billingStatusLine, discountLine } from "@teamna/ui/owner/billing.tsx";
import { newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { formatDate, formatDateTime } from "@teamna/web-client/lib/format.ts";
import { useAdminBilling, useBillingAction, type BillingActionPath } from "../lib/cc-api.ts";
import { billingCell, eventDetail, eventName } from "./billing-format.ts";

/**
 * The Billing card of one business (subscription billing spec, section 6.2): the plan as Stripe
 * has it, the support fields, links into the Stripe dashboard, the actions, and the last 10 billing
 * events. Each action opens a panel that asks for a reason; the API writes it to the billing feed
 * and the audit log with the staff email.
 */

type ActionId = "trial" | "discount" | "removeDiscount" | "keep" | "refund" | "pause" | "resume" | "credit" | "price" | "pilotOn" | "pilotOff";

export function BillingCard({ businessId, timezone }: { businessId: string; timezone: string }) {
  const billing = useAdminBilling(businessId);
  const [open, setOpen] = useState<ActionId | null>(null);

  let body: ReactNode;
  if (billing.isLoading && !billing.data) body = <SkeletonList rows={2} />;
  else if (!billing.data) body = <ErrorState error={billing.error} onRetry={() => void billing.refetch()} />;
  else if (billing.data.pilotSince) body = <PilotBody data={billing.data} timezone={timezone} onAction={setOpen} />;
  else if (!billing.data.enabled) {
    body = (
      <>
        <p className="mb-4 text-[14px] text-ink-2">Billing is off on this server (BILLING_PROVIDER=off).</p>
        <PilotButton data={billing.data} onAction={setOpen} />
      </>
    );
  } else body = <CardBody data={billing.data} timezone={timezone} onAction={setOpen} />;

  return (
    <section aria-labelledby="billing-heading" className="mb-8 rounded-card border border-line bg-paper p-5 shadow-card">
      <h2 id="billing-heading" className="mb-3 text-[17px]">
        Billing
      </h2>
      {body}
      {billing.data && open ? (
        <ActionPanel action={open} data={billing.data} businessId={businessId} timezone={timezone} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow text-ink-2">{label}</dt>
      <dd className="mt-0.5 text-[14px] [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** A trial with no card, an ended plan, or no plan yet can become a pilot. A card or a paid plan cannot. */
function canStartPilot(data: AdminBilling): boolean {
  return data.state === null || data.state === "ended" || (data.state === "trialing" && data.card === null);
}

/** Why the switch is off, in the words of the service refusal (`startPilot`). */
function pilotRefusalOf(data: AdminBilling): string | undefined {
  if (canStartPilot(data)) return undefined;
  if (data.state === "past_due" && data.card === null) return "This business is past due. Change the trial end first, then make it a pilot.";
  return "A pilot needs a trial with no card. For a card or a paid plan, apply a 100% discount.";
}

function PilotButton({ data, onAction }: { data: AdminBilling; onAction: (id: ActionId) => void }) {
  const refusal = pilotRefusalOf(data);
  return (
    <Button variant="secondary" onClick={() => onAction("pilotOn")} disabled={refusal !== undefined} title={refusal}>
      Make it a pilot
    </Button>
  );
}

/** A pilot: no plan to show, only since when, the switch back, and the trail. */
function PilotBody({ data, timezone, onAction }: { data: AdminBilling; timezone: string; onAction: (id: ActionId) => void }) {
  const cell = billingCell(null, "pilot", new Date());
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge tone={cell.tone}>{cell.label}</Badge>
        <p className="text-[14px]">In the Teamna pilot since {formatDate(data.pilotSince!, timezone)}. No trial and no charge.</p>
      </div>
      <div className="mb-6 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => onAction("pilotOff")}>
          End the pilot
        </Button>
      </div>
      <EventsTable events={data.events} timezone={timezone} />
    </>
  );
}

function EventsTable({ events, timezone }: { events: BillingEvent[]; timezone: string }) {
  return (
    <>
      <h3 className="mb-2 text-[15px] font-semibold">Last billing events</h3>
      <DataTable
        columns={eventColumns(timezone)}
        rows={events}
        rowKey={(row) => row.id}
        caption="Last billing events"
        density="compact"
        countLabel={(count) => `Last ${count}`}
        empty={{ title: "No billing events", message: "Nothing has happened in billing for this business yet." }}
      />
    </>
  );
}

function CardBody({ data, timezone, onAction }: { data: AdminBilling; timezone: string; onAction: (id: ActionId) => void }) {
  const now = new Date();
  const cell = billingCell({ ...data, hasCard: data.card !== null, hasDiscount: data.discount !== null }, null, now);
  const date = (value: string | null) => (value ? formatDate(value, timezone) : "None");
  const paid = data.invoices.some((invoice) => invoice.status === "paid" && invoice.amountPaidFils > invoice.refundedFils);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge tone={cell.tone}>{cell.label}</Badge>
        <p className="text-[14px]">{billingStatusLine(data, now, timezone)}</p>
      </div>
      <dl className="mb-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Trial end">{date(data.trialEndsAt)}</Fact>
        <Fact label="Next charge">{data.nextCharge ? `${formatAED(data.nextCharge.amountFils)} on ${formatDate(data.nextCharge.at, timezone)}` : "None"}</Fact>
        <Fact label="Price">
          {formatAED(data.priceFils)} a month{data.onCurrentPrice ? "" : " (an older price)"}
        </Fact>
        <Fact label="Discount">{data.discount ? discountLine(data.discount, timezone) : "None"}</Fact>
        <Fact label="Credit">{data.creditFils > 0 ? formatAED(data.creditFils) : "None"}</Fact>
        <Fact label="Card">{data.card ? `${data.card.brand} ${data.card.last4}, ${String(data.card.expMonth).padStart(2, "0")}/${data.card.expYear}` : "No card"}</Fact>
        <Fact label="Billing email">{data.billingEmail ?? "None"}</Fact>
        <Fact label="Access kept until">{data.accessOverrideUntil ? formatDateTime(data.accessOverrideUntil, timezone) : "Not kept"}</Fact>
        <Fact label="Stripe status">{data.providerStatus ?? "None"}</Fact>
        <Fact label="Read-only since">{data.readOnlySince ? formatDateTime(data.readOnlySince, timezone) : "Not read-only"}</Fact>
        <Fact label="Last sync">{data.syncedAt ? formatDateTime(data.syncedAt, timezone) : "Never"}</Fact>
        <Fact label="Stripe">
          {data.dashboard ? (
            <span className="flex flex-wrap gap-x-3">
              <a className="text-accent-strong underline" href={data.dashboard.customerUrl} target="_blank" rel="noreferrer">
                Customer
              </a>
              <a className="text-accent-strong underline" href={data.dashboard.subscriptionUrl} target="_blank" rel="noreferrer">
                Subscription
              </a>
            </span>
          ) : (
            "No links (fake provider)"
          )}
        </Fact>
      </dl>

      <div className="mb-6 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => onAction("trial")} disabled={data.state === "ended"}>
          Change trial end
        </Button>
        <Button variant="secondary" onClick={() => onAction("discount")}>
          {data.discount ? "Replace discount" : "Apply discount"}
        </Button>
        {data.discount ? (
          <Button variant="secondary" onClick={() => onAction("removeDiscount")}>
            Remove discount
          </Button>
        ) : null}
        <Button variant="secondary" onClick={() => onAction("keep")}>
          Keep access while unpaid
        </Button>
        <Button variant="secondary" onClick={() => onAction("refund")} disabled={!paid} title={paid ? undefined : "No paid invoice to refund."}>
          Refund
        </Button>
        {data.state === "paused" ? (
          <Button variant="secondary" onClick={() => onAction("resume")}>
            Resume billing
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => onAction("pause")} disabled={data.state !== "active"} title={data.state === "active" ? undefined : "Pause works on a paid plan."}>
            Pause billing
          </Button>
        )}
        <Button variant="secondary" onClick={() => onAction("credit")}>
          Add credit
        </Button>
        {data.onCurrentPrice ? null : (
          <Button variant="secondary" onClick={() => onAction("price")}>
            Move to current price
          </Button>
        )}
        <PilotButton data={data} onAction={onAction} />
      </div>

      <EventsTable events={data.events} timezone={timezone} />
    </>
  );
}

export function eventColumns(timezone?: string, withBusiness = false): DataTableColumn<BillingEvent>[] {
  return [
    {
      id: "when",
      header: "When",
      sortValue: (row) => Date.parse(row.createdAt),
      cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.createdAt, timezone)}</span>,
    },
    ...(withBusiness
      ? [{ id: "business", header: "Business", sortValue: (row: BillingEvent) => row.businessName, cell: (row: BillingEvent) => row.businessName }]
      : []),
    { id: "what", header: "What", sortValue: (row) => row.kind, cell: (row) => eventName(row.kind) },
    { id: "detail", header: "Detail", cell: (row) => eventDetail(row) },
    {
      id: "who",
      header: "By and why",
      cell: (row) =>
        row.actorStaffEmail ? (
          <div className="min-w-0">
            <p className="text-[13px]">{row.actorStaffEmail}</p>
            {row.reason ? <p className="text-[12px] text-ink-2">{row.reason}</p> : null}
          </div>
        ) : (
          <span className="text-ink-3">System</span>
        ),
    },
  ];
}

// --- the action panel -----------------------------------------------------------------

const TITLES: Record<ActionId, string> = {
  trial: "Change the trial end",
  discount: "Apply a discount",
  removeDiscount: "Remove the discount",
  keep: "Keep access while unpaid",
  refund: "Refund an invoice",
  pause: "Pause billing",
  resume: "Resume billing",
  credit: "Add credit",
  price: "Move to the current price",
  pilotOn: "Make it a pilot",
  pilotOff: "End the pilot",
};

/** AED as typed ("75" or "75.50") to fils, or null when it is not a positive amount. */
function filsFrom(text: string): number | null {
  const value = Number(text.trim());
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

function ActionPanel({
  action,
  data,
  businessId,
  timezone,
  onClose,
}: {
  action: ActionId;
  data: AdminBilling;
  businessId: string;
  timezone: string;
  onClose: () => void;
}) {
  const run = useBillingAction(businessId);
  // One key while the panel is open: a second press after a lost answer replays the first.
  const [key] = useState(newIdempotencyKey);
  const [reason, setReason] = useState("");
  const [today] = useState(() => localDate(new Date(), timezone));
  const [day, setDay] = useState(() => localDate(new Date(new Date().getTime() + 7 * 86_400_000), timezone));
  const [endNow, setEndNow] = useState<"date" | "now">("date");
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [amount, setAmount] = useState("");
  const [duration, setDuration] = useState<"once" | "repeating" | "forever">("repeating");
  const [months, setMonths] = useState("3");
  const [days, setDays] = useState("7");
  const [invoiceId, setInvoiceId] = useState(data.invoices.find((invoice) => invoice.status === "paid")?.id ?? "");
  const [pauseAccess, setPauseAccess] = useState<"full" | "read_only">("full");
  const [formError, setFormError] = useState<string | null>(null);

  const noon = (value: string) => zonedDateTime(value, "12:00", timezone).toISOString();

  // The pilot switch may have committed before its answer failed. The hook reads the card again on
  // an error; when the card then shows the asked state, the action is done (review F6.3).
  const pilotDone = run.isError && ((action === "pilotOn" && data.pilotSince !== null) || (action === "pilotOff" && data.pilotSince === null));
  useEffect(() => {
    if (!pilotDone) return;
    showToast(`${TITLES[action]}: done.`);
    onClose();
  }, [pilotDone, action, onClose]);

  function request(): { path: BillingActionPath; body: Record<string, unknown> } | string {
    const why = { reason: reason.trim() };
    switch (action) {
      case "trial":
        return { path: "trial-end", body: { trialEnd: endNow === "now" ? null : noon(day), ...why } };
      case "discount": {
        const value = kind === "percent" ? Number(amount) : filsFrom(amount);
        if (!value || (kind === "percent" && (value < 1 || value > 100 || !Number.isInteger(value)))) return "Give a whole percent from 1 to 100, or an amount in AED.";
        const count = Number(months);
        if (duration === "repeating" && (!Number.isInteger(count) || count < 1 || count > 36)) return "Give the months, from 1 to 36.";
        return {
          path: "discount",
          body: {
            percentOff: kind === "percent" ? value : null,
            amountOffFils: kind === "amount" ? value : null,
            duration,
            months: duration === "repeating" ? count : null,
            ...why,
          },
        };
      }
      case "removeDiscount":
        return { path: "discount/remove", body: why };
      case "keep": {
        const count = Number(days);
        if (!Number.isInteger(count) || count < 0 || count > 90) return "Give the days, from 0 to 90. 0 ends it now.";
        return { path: "keep-access", body: { days: count, ...why } };
      }
      case "refund": {
        if (!invoiceId) return "Choose an invoice.";
        const fils = amount.trim() === "" ? null : filsFrom(amount);
        if (amount.trim() !== "" && fils === null) return "Give the amount in AED, or leave it empty for the full amount left.";
        return { path: "refund", body: { invoiceId, amountFils: fils, ...why } };
      }
      case "pause":
        return { path: "pause", body: { until: noon(day), access: pauseAccess, ...why } };
      case "resume":
        return { path: "resume", body: why };
      case "credit": {
        const fils = filsFrom(amount);
        if (!fils) return "Give the amount in AED.";
        return { path: "credit", body: { amountFils: fils, ...why } };
      }
      case "price":
        return { path: "move-to-current-price", body: why };
      case "pilotOn":
        return { path: "pilot", body: { pilot: true, ...why } };
      case "pilotOff":
        return { path: "pilot", body: { pilot: false, ...why } };
    }
  }

  const submit = () => {
    const built = request();
    if (typeof built === "string") {
      setFormError(built);
      return;
    }
    setFormError(null);
    run.mutate(
      { ...built, idempotencyKey: key },
      {
        onSuccess: () => {
          showToast(`${TITLES[action]}: done.`);
          onClose();
        },
      },
    );
  };

  const refundable = data.invoices.filter((invoice) => invoice.status === "paid" && invoice.amountPaidFils > invoice.refundedFils);

  return (
    <Panel
      open
      onClose={onClose}
      title={TITLES[action]}
      subtitle="Each change needs a reason. It goes to the billing feed and the audit log with your email."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={action === "refund" || action === "removeDiscount" ? "danger" : "primary"} loading={run.isPending} disabled={reason.trim().length < 3} onClick={submit}>
            {TITLES[action]}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {run.error ? <InlineError error={run.error} /> : null}
        {formError ? <p className="text-[13px] font-medium text-cancelled">{formError}</p> : null}

        {action === "trial" ? (
          <>
            <RadioGroup
              legend="New trial end"
              value={endNow}
              onChange={setEndNow}
              options={[
                { value: "date", label: "On a date", hint: "Free until that day. An open invoice is voided. The bill date moves to that day." },
                { value: "now", label: "End the trial now", hint: "Stripe charges the card at once. With no card, the grace starts." },
              ]}
            />
            {endNow === "date" ? <Field label="Trial ends on" type="date" min={today} value={day} onChange={(event) => setDay(event.target.value)} /> : null}
          </>
        ) : null}

        {action === "discount" ? (
          <>
            <SegmentedControl
              label="Discount type"
              value={kind}
              onChange={setKind}
              options={[
                { value: "percent", label: "Percent" },
                { value: "amount", label: "AED amount" },
              ]}
            />
            <Field
              label={kind === "percent" ? "Percent off" : "AED off each month"}
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              hint={kind === "percent" ? "1 to 100. 100 means the plan costs nothing; no card is needed." : `The price is ${formatAED(data.priceFils)}.`}
            />
            <RadioGroup
              legend="For how long"
              value={duration}
              onChange={setDuration}
              options={[
                { value: "once", label: "The next invoice only" },
                { value: "repeating", label: "A number of months", hint: "Counted in calendar months from today. A paused month counts." },
                { value: "forever", label: "No end date" },
              ]}
            />
            {duration === "repeating" ? <Field label="Months" type="number" inputMode="numeric" value={months} onChange={(event) => setMonths(event.target.value)} /> : null}
            {data.discount ? <p className="text-[13px] text-ink-2">This replaces the discount now: {discountLine(data.discount, timezone)}.</p> : null}
          </>
        ) : null}

        {action === "keep" ? (
          <Field
            label="Days of full access"
            type="number"
            inputMode="numeric"
            value={days}
            onChange={(event) => setDays(event.target.value)}
            hint="Desk stays full for the owner while unpaid. 0 ends it now. Stripe keeps trying the card."
          />
        ) : null}

        {action === "refund" ? (
          <>
            <SelectField label="Invoice" value={invoiceId} onChange={(event) => setInvoiceId(event.target.value)}>
              {refundable.map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {invoiceLabel(invoice, timezone)}
                </option>
              ))}
            </SelectField>
            <Field
              label="Amount in AED"
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              hint="Leave it empty to refund the full amount left. Stripe sends a credit note."
            />
          </>
        ) : null}

        {action === "pause" ? (
          <>
            <Field label="Pause until" type="date" min={today} value={day} onChange={(event) => setDay(event.target.value)} hint="No charges until then. Invoices in between are voided." />
            <RadioGroup
              legend="Desk while paused"
              value={pauseAccess}
              onChange={setPauseAccess}
              options={[
                { value: "full", label: "Full access" },
                { value: "read_only", label: "Read-only for the owner and admins" },
              ]}
            />
          </>
        ) : null}

        {action === "credit" ? (
          <Field label="Credit in AED" type="number" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} hint="Stripe uses it on the next invoices before it charges the card." />
        ) : null}

        {action === "price" ? (
          <p className="text-[14px]">
            The business pays {formatAED(data.priceFils)} a month now. From its next period it pays the current list price. Nothing is charged now.
          </p>
        ) : null}

        {action === "pilotOn" ? (
          <p className="text-[14px]">
            The business gets Teamna free, with no trial and no end date. Desk stays full, and Teamna sends no billing emails.
            {data.state === "trialing" ? " Teamna cancels the trial in Stripe now." : ""} AI Assistant needs a grant.
          </p>
        ) : null}

        {action === "pilotOff" ? (
          <p className="text-[14px]">
            {data.enabled
              ? "The 7-day free trial starts today. After the trial, the business pays the plan."
              : "Billing is off on this server. The 7-day free trial starts when billing goes on."}
          </p>
        ) : null}

        <TextAreaField label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} hint="Why, in a few words. Required." rows={3} maxLength={500} />
      </div>
    </Panel>
  );
}

function invoiceLabel(invoice: BillingInvoiceView, timezone: string): string {
  const left = invoice.amountPaidFils - invoice.refundedFils;
  return `${invoice.number ?? invoice.id}, ${formatDate(invoice.createdAt, timezone)}, paid ${formatAED(invoice.amountPaidFils)}, ${formatAED(left)} left`;
}
